import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

export const getAuthAutoComplete = (type?: string, name?: string) => {
  if (type === 'email' || name === 'email' || name === 'loginEmail' || name === 'confirmEmail') {
    return 'username';
  }

  if (name === 'loginPassword') {
    return 'current-password';
  }

  if (type === 'password') {
    return 'new-password';
  }

  return undefined;
};

export const useSyncAutofillValue = (
  inputRef: RefObject<HTMLInputElement | null>,
  currentValue: unknown,
  onChange: ((value: string) => void) | undefined,
  enabled: boolean,
) => {
  useEffect(() => {
    if (!enabled || !onChange) {
      return undefined;
    }

    const input = inputRef.current;
    if (!input) {
      return undefined;
    }

    const sync = () => {
      if (input.value !== (currentValue ?? '')) {
        onChange(input.value);
      }
    };

    let intervalId: number | undefined;
    const startPolling = () => {
      sync();
      window.clearInterval(intervalId);
      intervalId = window.setInterval(sync, 150);
    };
    const stopPolling = () => {
      window.clearInterval(intervalId);
    };

    const onBlur = () => {
      sync();
      stopPolling();
    };

    input.addEventListener('input', sync);
    input.addEventListener('change', sync);
    input.addEventListener('focus', startPolling);
    input.addEventListener('blur', onBlur);

    if (document.activeElement === input) {
      startPolling();
    }

    return () => {
      stopPolling();
      input.removeEventListener('input', sync);
      input.removeEventListener('change', sync);
      input.removeEventListener('focus', startPolling);
      input.removeEventListener('blur', onBlur);
    };
  }, [currentValue, enabled, inputRef, onChange]);
};

const usePasswordVisibility = () => {
  const [isVisible, setIsVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const toggleVisibility = useCallback(
    (onChange?: (value: string) => void) => {
      const nextVisible = !isVisible;
      const input = inputRef.current;

      // Safari can fill a suggested password in the DOM without notifying React.
      // Copy that value into the form first, then switch type in this same click
      // so WebKit actually unmasks instead of remasking on re-render.
      if (input) {
        onChange?.(input.value);
        if (nextVisible) {
          input.style.setProperty('-webkit-text-security', 'none');
          input.type = 'text';
        } else {
          input.style.removeProperty('-webkit-text-security');
          input.type = 'password';
        }
      }

      setIsVisible(nextVisible);
    },
    [isVisible],
  );

  return {
    isVisible,
    inputRef,
    toggleVisibility,
    visibilityKey: isVisible ? 'shown' : 'hidden',
  };
};

export default usePasswordVisibility;
